import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { GlobalStateContext } from "context/global/GlobalContextProvider"
import React, { useContext, useEffect, useState } from "react"
import { usePromise } from "react-use"
import {
  faArrowRight,
  faSpinnerThird,
} from "@fortawesome/pro-regular-svg-icons"
import * as styles from "./dashboardSummary.module.css"
import Difference from "../DashboardDifference/DashboardDifference"
import { Link } from "@reach/router"
import { getForDashboard } from "services/dashboard"
import { IconProp } from "@fortawesome/fontawesome-svg-core"
import { roundNumber } from "services/helpers"
import { useDoubleRequestPreventor } from "hooks/useDoubleRequestPreventor"

interface DashboardSummaryProps {
  selectedOption: any
  refreshObserver: string
  items: {
    title: string
    apiRoute: string
    refer: string
    fontAwesomeIcon: IconProp
    iconColor?: string
    iconBackgroundColor?: string
    bgColor: string
    loading: boolean | true
  }[]
}

const DashboardSummary = ({
  selectedOption,
  refreshObserver,
  items,
}: DashboardSummaryProps) => {
  const { selectedOrganizations, organization } = useContext(GlobalStateContext)
  const [data, setData] = useState<any>({})
  const fromPromise = usePromise()
  const doubleRequestPreventor = useDoubleRequestPreventor()

  useEffect(() => {
    const fetchData = async () => {
      const params: any = {
        searchBy: selectedOption,
        organizations: selectedOrganizations,
        organization: organization,
      }

      if (doubleRequestPreventor.check(params)) return

      items.map((item) => (item.loading = true))
      const responses = await Promise.all(
        items.map(async (item) => {
          const result: any = await getForDashboard({
            ...params,
            route: item.apiRoute,
          })
          setData((prevData: any) => {
            const newData = { ...prevData }
            newData[item.apiRoute] = {
              data: result,
              loading: result === undefined || result?.error,
            }
            return newData
          })
        })
      )
      // invoices, delivery-notes, wastes
      return responses
    }
    fetchData()
  }, [
    selectedOrganizations,
    organization,
    items,
    fromPromise,
    refreshObserver,
    selectedOption,
  ])
  return (
    <div className={styles.dashSummaryContainer}>
      <h3>Summary</h3>
      <div className={styles.dashSummaryWrapper}>
        {items.map((item, index) => (
          <React.Fragment key={index}>
            {index % 2 === 0 && (
              <div
                className={`${styles.dashSummaryItemContainer} ${
                  index === items.length - 1 && items.length % 2 !== 0
                    ? styles.dashSummaryItemContainerOnly
                    : ""
                }`}
              >
                {[items[index], items[index + 1]].map(
                  (subItem, subIndex) =>
                    subItem && (
                      <div
                        className={styles.dashSummaryItem}
                        style={{ backgroundColor: subItem.bgColor }}
                        key={subIndex}
                      >
                        <div className={styles.dashSummaryItemHeader}>
                          <div
                            className={styles.dashSummaryItemIcon}
                            style={{
                              backgroundColor: subItem.iconBackgroundColor,
                              color: subItem.iconColor,
                            }}
                          >
                            <FontAwesomeIcon icon={subItem.fontAwesomeIcon} />
                          </div>
                          <div className={styles.dashSummaryItemIntro}>
                            <h3>
                              {data[subItem.apiRoute]?.loading ? (
                                <div>
                                  <FontAwesomeIcon icon={faSpinnerThird} spin />
                                </div>
                              ) : (
                                roundNumber(
                                  data[subItem.apiRoute]?.data?.totalCost ??
                                    data[subItem.apiRoute]?.data
                                      ?.totalRevenue ??
                                    data[subItem.apiRoute]?.data?.totalSales ??
                                    data[subItem.apiRoute]?.data
                                      ?.totalElements ??
                                    0,
                                  2
                                )
                              )}
                            </h3>
                            <p>Total value {subItem.title}</p>
                          </div>
                          <Link
                            className={styles.dashSummaryItemButton}
                            to={subItem.refer}
                          >
                            <FontAwesomeIcon icon={faArrowRight} />
                          </Link>
                        </div>
                        <div className={styles.dashSummaryItemBody}>
                          <div className={styles.dashSummaryItemData}>
                            <>
                              {(data[subItem.apiRoute]?.data
                                ?.totalCostPercentage !== undefined ||
                                data[subItem.apiRoute]?.data?.percentage !==
                                  undefined) && (
                                <Difference
                                  percentage={
                                    data[subItem.apiRoute]?.data
                                      ?.totalCostPercentage ||
                                    data[subItem.apiRoute]?.data?.percentage
                                  }
                                  showIcon
                                  label="Var to previous period"
                                />
                              )}
                              {data[subItem.apiRoute]?.data
                                ?.totalCostToSalesPercentage !== undefined && (
                                <Difference
                                  percentage={
                                    data[subItem.apiRoute]?.data
                                      ?.totalCostToSalesPercentage
                                  }
                                  showGrey
                                  label="Percentage of sales"
                                />
                              )}
                            </>
                          </div>
                        </div>
                      </div>
                    )
                )}
              </div>
            )}
          </React.Fragment>
        ))}
      </div>
    </div>
  )
}

export default DashboardSummary
